import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Package,
  ClipboardList,
  Calculator,
  GitCompare,
  BarChart3,
  LayoutDashboard,
  FileSpreadsheet,
  DatabaseBackup,
  ShieldCheck,
  Smartphone,
  FolderOpen,
  DollarSign,
  Clock,
  TrendingUp,
  Check,
  X,
  Lock,
  Zap,
  CreditCard,
  Loader2,
  Sparkles,
  RefreshCw,
  CalendarX,
} from "lucide-react";
import { API } from "@/lib/api";
import { PLANOS, irParaPagamento, rastrear } from "@/lib/planos";

// Mesmo visual da pagina de vendas do Gamma, agora dentro do proprio app.
const COR = {
  fundo: "#123332",
  fundoAlt: "#1c4241",
  cartao: "#1c4241",
  borda: "#315251",
  titulo: "#FFD9BE",
  texto: "#F9EEE7",
  suave: "#c5d3d0",
  coral: "#EF9C82",
};
const SERIFA = { fontFamily: "'Quattrocento', Georgia, serif" };
const FOTO_HERO =
  "https://imgproxy.gamma.app/resize/quality:80/resizing_type:fit/height:640/https://cdn.gamma.app/2ah78w0pl6rtq2g/28b69bf6de6c4e97b8442ca75df5ab57/original/Captura-de-tela-2026-04-13-132836.png";
const VIDEO = "https://www.youtube-nocookie.com/embed/oAx5lg9ll-w";

const FUNCOES = [
  { icon: Package, titulo: "Produtos e insumos", texto: "Cadastre tudo o que você usa, com unidade, custo, categoria e busca rápida." },
  { icon: ClipboardList, titulo: "Fichas técnicas", texto: "Monte cada procedimento com o produto principal e os insumos. Duplique fichas e comece pelos modelos prontos." },
  { icon: Calculator, titulo: "Precificação completa", texto: "Impostos, taxa da maquininha, comissão, perdas e custos entram no cálculo. Você vê a margem real e o preço que dá lucro." },
  { icon: GitCompare, titulo: "Comparativo de procedimentos", texto: "Coloque os procedimentos lado a lado e descubra quais trazem mais lucro para a clínica." },
  { icon: BarChart3, titulo: "Relatório de margens", texto: "Saiba na hora o que dá lucro e o que dá prejuízo, com a margem de cada procedimento." },
  { icon: LayoutDashboard, titulo: "Painel com indicadores", texto: "Faturamento potencial, margem média e quantos procedimentos estão no vermelho, num só lugar." },
  { icon: FileSpreadsheet, titulo: "Exportação para Excel", texto: "Leve o relatório geral, as fichas técnicas e as precificações para planilha com um clique." },
  { icon: DatabaseBackup, titulo: "Backup automático", texto: "Seus dados ganham uma cópia de segurança todo dia, e você pode baixar tudo quando quiser." },
  { icon: ShieldCheck, titulo: "Acesso protegido", texto: "Login com senha e verificação em duas etapas opcional, com o aplicativo autenticador do celular." },
  { icon: Smartphone, titulo: "No computador e no celular", texto: "Funciona direto no navegador, sem instalar nada." },
];

const MODELOS = ["Toxina botulínica", "Preenchimento com ácido hialurônico", "Limpeza de pele", "Peeling químico", "Microagulhamento"];

const DORES = [
  { icon: FolderOpen, titulo: "Perda de informações", texto: "Fichas e custos centralizados, seguros e acessíveis de qualquer lugar. Nada de papel perdido." },
  { icon: DollarSign, titulo: "Dificuldade em precificar", texto: "Tenha clareza e lucratividade real em cada protocolo. Pare de cobrar no achismo." },
  { icon: Clock, titulo: "Tempo perdido com contas", texto: "O cálculo de custo, margem e preço é automático. Sobra tempo para o que importa: suas pacientes." },
  { icon: TrendingUp, titulo: "Lucro invisível", texto: "Descubra quanto sobra de verdade em cada procedimento e tome decisões com base em números." },
];

const DEPOIMENTOS = [
  { texto: "Antes, a gestão da minha clínica era um caos. Com o app, tudo se tornou simples e organizado. A precificação dos protocolos nunca foi tão clara!", nome: "Tirzá Cristina", papel: "Proprietária de Clínica de Estética" },
  { texto: "Ganhei tempo e profissionalismo. O app foi um investimento que se pagou em semanas. Não consigo imaginar minha rotina sem ele!", nome: "Charlyse", papel: "Esteticista Profissional" },
];

const PERGUNTAS = [
  { p: "O que é o app?", r: "É um sistema de precificação para clínicas de estética. Você cadastra produtos e insumos, monta as fichas técnicas dos procedimentos e o app calcula custo, margem e o preço que dá lucro. Também traz relatório de margens, comparativo entre procedimentos e exportação para Excel." },
  { p: "Quanto custa?", r: "Você escolhe o plano: Mensal (R$ 25 por mês), Trimestral (R$ 50 a cada 3 meses, R$ 16,67 por mês) ou Anual (R$ 180 por ano, R$ 15 por mês). Todos dão acesso a todas as funções." },
  { p: "Como funciona a cobrança?", r: "A assinatura é cobrada no cartão de crédito e renova sozinha ao fim de cada período. O pagamento é processado pelo Stripe, e o recibo chega no seu e-mail." },
  { p: "Posso cancelar quando quiser?", r: "Sim, sem multa. O cancelamento é feito pela área Minha conta, dentro do app. Você continua usando até o fim do período que já pagou." },
  { p: "Posso pagar com Pix?", r: "Por enquanto a assinatura é só no cartão de crédito, porque é o meio que permite a renovação automática." },
  { p: "E se eu me arrepender?", r: "Você tem 7 dias a partir da primeira cobrança para desistir e receber o valor de volta, como garante o Código de Defesa do Consumidor. Basta falar com o suporte." },
  { p: "Preciso ter experiência com tecnologia?", r: "Não! O app foi feito para ser simples. Se você usa o celular no dia a dia, consegue usar o app sem dificuldade." },
  { p: "Meus dados ficam seguros?", r: "Sim. Os dados do cartão ficam só com o Stripe, nunca no app. Cada cliente vê apenas os próprios dados, existe backup diário e você pode ativar a verificação em duas etapas." },
];

function BotaoPlanos({ children = "Ver os planos", className = "" }) {
  return (
    <a
      href="#planos"
      className={`inline-flex items-center justify-center rounded-md px-7 py-3 text-base font-semibold shadow-lg transition-transform hover:-translate-y-0.5 ${className}`}
      style={{ background: COR.coral, color: COR.fundo }}
      data-testid="landing-ver-planos"
    >
      {children}
    </a>
  );
}

function Secao({ id, alt = false, children, className = "" }) {
  return (
    <section id={id} style={{ background: alt ? COR.fundoAlt : COR.fundo }} className={`scroll-mt-16 px-4 py-16 md:py-20 ${className}`}>
      <div className="mx-auto max-w-6xl">{children}</div>
    </section>
  );
}

function Titulo({ children, className = "" }) {
  return (
    <h2 className={`text-3xl md:text-4xl leading-tight ${className}`} style={{ ...SERIFA, color: COR.titulo }}>
      {children}
    </h2>
  );
}

export default function Landing() {
  const [carregando, setCarregando] = useState(null);
  const [fotoOk, setFotoOk] = useState(true);
  const [empresa, setEmpresa] = useState(null);

  useEffect(() => {
    document.title = "Aesthetica Pricing Pro | Precificação para clínicas de estética";
    rastrear("ViewContent", { content_name: "Pagina de planos" });
    axios.get(`${API}/publico/empresa`).then((r) => setEmpresa(r.data)).catch(() => {});
  }, []);

  const assinar = async (planoId) => {
    setCarregando(planoId);
    try {
      await irParaPagamento(planoId);
    } catch (err) {
      setCarregando(null);
      const detalhe = err.response?.data?.detail;
      toast.error(typeof detalhe === "string" ? detalhe : "Não foi possível abrir o pagamento. Tente de novo.");
    }
  };

  return (
    <div style={{ background: COR.fundo, color: COR.texto, fontFamily: "'Quattrocento Sans', Arial, sans-serif" }} className="min-h-screen" data-testid="landing-page">
      {/* Topo */}
      <header className="sticky top-0 z-40 border-b backdrop-blur" style={{ background: "rgba(18,51,50,0.88)", borderColor: COR.borda }}>
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <a href="#inicio" className="leading-none">
            <span className="block text-xl" style={{ ...SERIFA, color: COR.titulo }}>Aesthetica</span>
            <span className="block text-[11px] tracking-wide" style={{ color: COR.suave }}>Pricing Pro</span>
          </a>
          <nav className="flex items-center gap-2 sm:gap-4">
            <a href="#planos" className="hidden text-sm sm:inline hover:underline" style={{ color: COR.texto }}>Planos</a>
            <a href="#duvidas" className="hidden text-sm sm:inline hover:underline" style={{ color: COR.texto }}>Dúvidas</a>
            <Link
              to="/login"
              className="rounded-md border px-4 py-2 text-sm font-semibold transition-colors hover:bg-white/10"
              style={{ borderColor: COR.coral, color: COR.titulo }}
              data-testid="landing-entrar"
            >
              Entrar
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <Secao id="inicio">
        <div className="grid items-center gap-10 md:grid-cols-2">
          <div>
            <h1 className="text-4xl leading-tight md:text-5xl" style={{ ...SERIFA, color: COR.titulo }}>
              Transforme Sua Clínica de Estética: Gestão Inteligente e Precificação Perfeita!
            </h1>
            <p className="mt-6 text-lg leading-relaxed" style={{ color: COR.texto }}>
              Chega de perder tempo com papelada, incertezas na precificação e desorganização. Descubra o app que está
              revolucionando a gestão de clínicas de estética em todo o Brasil, a partir de <strong>R$ 25 por mês</strong>.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <BotaoPlanos>Quero assinar</BotaoPlanos>
              <Link to="/login" className="text-sm underline-offset-4 hover:underline" style={{ color: COR.suave }}>
                Já sou assinante, entrar
              </Link>
            </div>
          </div>
          {fotoOk && (
            <div className="overflow-hidden rounded-xl shadow-2xl">
              <img
                src={FOTO_HERO}
                alt="Profissional de estética apresentando o app de precificação"
                className="h-full w-full object-cover"
                onError={() => setFotoOk(false)}
              />
            </div>
          )}
        </div>
      </Secao>

      {/* Video */}
      <Secao alt>
        <div className="text-center">
          <Titulo>Veja Como o App Revoluciona Sua Rotina</Titulo>
          <p className="mx-auto mt-4 max-w-3xl text-lg" style={{ color: COR.suave }}>
            Assista ao vídeo e descubra o poder do sistema. Em poucos minutos, você vai entender por que centenas de
            profissionais já adotaram essa solução para organizar, precificar e lucrar mais.
          </p>
        </div>
        <div className="mx-auto mt-10 max-w-4xl overflow-hidden rounded-xl shadow-2xl" style={{ aspectRatio: "16 / 9" }}>
          <iframe
            src={VIDEO}
            title="Como funciona o Aesthetica Pricing Pro"
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="h-full w-full border-0"
          />
        </div>
      </Secao>

      {/* Sem / com o app */}
      <Secao>
        <div className="text-center">
          <Titulo>Chega de Papelada e Incertezas!</Titulo>
          <p className="mx-auto mt-4 max-w-3xl text-lg" style={{ color: COR.suave }}>
            Você sabe exatamente quanto lucra em cada protocolo? Sabe se está cobrando o valor certo pelos seus
            procedimentos? Se a resposta for não, você não está sozinha, e existe uma solução simples e acessível.
          </p>
        </div>
        <div className="mx-auto mt-10 grid max-w-4xl gap-6 md:grid-cols-2">
          <div className="rounded-xl border p-6" style={{ borderColor: COR.borda, background: COR.cartao }}>
            <div className="flex items-center gap-2 text-xl" style={SERIFA}>
              <X className="h-6 w-6 text-rose-300" /> Sem o App
            </div>
            <p className="mt-3" style={{ color: COR.suave }}>Preços no achismo, fichas em papel, tempo desperdiçado e lucro invisível.</p>
          </div>
          <div className="rounded-xl border p-6" style={{ borderColor: COR.coral, background: COR.cartao }}>
            <div className="flex items-center gap-2 text-xl" style={SERIFA}>
              <Check className="h-6 w-6" style={{ color: COR.coral }} /> Com o App
            </div>
            <p className="mt-3" style={{ color: COR.suave }}>Precificação precisa, tudo digital, mais tempo para suas pacientes e lucro real.</p>
          </div>
        </div>
      </Secao>

      {/* Funcoes */}
      <Secao alt id="funcoes">
        <div className="text-center">
          <Titulo>Tudo o Que o App Faz por Você</Titulo>
          <p className="mx-auto mt-4 max-w-3xl text-lg" style={{ color: COR.suave }}>
            Um sistema completo, feito especialmente para profissionais de estética que querem crescer com organização e
            rentabilidade.
          </p>
        </div>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FUNCOES.map((f) => (
            <div key={f.titulo} className="rounded-xl border p-6" style={{ borderColor: COR.borda, background: COR.fundo }}>
              <f.icon className="h-7 w-7" style={{ color: COR.coral }} />
              <h3 className="mt-4 text-xl" style={{ ...SERIFA, color: COR.titulo }}>{f.titulo}</h3>
              <p className="mt-2 leading-relaxed" style={{ color: COR.suave }}>{f.texto}</p>
            </div>
          ))}
          <div className="rounded-xl border p-6" style={{ borderColor: COR.coral, background: COR.fundo }}>
            <Sparkles className="h-7 w-7" style={{ color: COR.coral }} />
            <h3 className="mt-4 text-xl" style={{ ...SERIFA, color: COR.titulo }}>Modelos prontos</h3>
            <ul className="mt-2 space-y-1" style={{ color: COR.suave }}>
              {MODELOS.map((m) => (
                <li key={m} className="flex items-start gap-2"><Check className="mt-1 h-4 w-4 shrink-0" style={{ color: COR.coral }} />{m}</li>
              ))}
            </ul>
          </div>
        </div>
      </Secao>

      {/* Dores */}
      <Secao>
        <div className="text-center">
          <Titulo>Diga Adeus aos Problemas Comuns da Sua Clínica</Titulo>
          <p className="mx-auto mt-4 max-w-3xl text-lg" style={{ color: COR.suave }}>
            Reconhece algum desses desafios no dia a dia? Com o app certo, cada um deles deixa de existir.
          </p>
        </div>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {DORES.map((d) => (
            <div key={d.titulo} className="rounded-xl border p-6" style={{ borderColor: COR.borda, background: COR.cartao }}>
              <d.icon className="h-7 w-7" style={{ color: COR.coral }} />
              <h3 className="mt-4 text-xl" style={{ ...SERIFA, color: COR.titulo }}>{d.titulo}</h3>
              <p className="mt-2 leading-relaxed" style={{ color: COR.suave }}>{d.texto}</p>
            </div>
          ))}
        </div>
      </Secao>

      {/* Depoimentos e numeros */}
      <Secao alt>
        <div className="text-center">
          <Titulo>O Que Nossas Usuárias Dizem</Titulo>
          <p className="mx-auto mt-4 max-w-3xl text-lg" style={{ color: COR.suave }}>
            Profissionais reais, resultados reais. Veja como o app mudou a rotina de quem já usa.
          </p>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {DEPOIMENTOS.map((d) => (
            <figure key={d.nome} className="rounded-xl border p-6" style={{ borderColor: COR.borda, background: COR.fundo }}>
              <blockquote className="text-lg italic leading-relaxed" style={{ color: COR.texto }}>“{d.texto}”</blockquote>
              <figcaption className="mt-4 text-sm" style={{ color: COR.suave }}>
                <strong style={{ color: COR.titulo }}>{d.nome}</strong>, {d.papel}
              </figcaption>
            </figure>
          ))}
        </div>
        <div className="mt-10 grid gap-5 text-center sm:grid-cols-3">
          {[
            { n: "Centenas", t: "de profissionais usando" },
            { n: "100%", t: "digital e seguro" },
            { n: "Sem multa", t: "cancele quando quiser" },
          ].map((x) => (
            <div key={x.t} className="rounded-xl border p-6" style={{ borderColor: COR.borda }}>
              <div className="text-3xl" style={{ ...SERIFA, color: COR.coral }}>{x.n}</div>
              <div className="mt-1" style={{ color: COR.suave }}>{x.t}</div>
            </div>
          ))}
        </div>
      </Secao>

      {/* Planos */}
      <Secao id="planos">
        <div className="text-center">
          <Titulo>Escolha Seu Plano</Titulo>
          <p className="mx-auto mt-4 max-w-3xl text-lg" style={{ color: COR.suave }}>
            Todos os planos liberam todas as funções do app. Quanto maior o período, menor o valor por mês.
          </p>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {PLANOS.map((p) => (
            <div
              key={p.id}
              className="relative flex flex-col rounded-2xl border p-7 shadow-xl"
              style={{ borderColor: p.destaque ? COR.coral : COR.borda, background: COR.cartao }}
              data-testid={`plano-${p.id}`}
            >
              {p.destaque && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-3 py-1 text-xs font-bold" style={{ background: COR.coral, color: COR.fundo }}>
                  Melhor custo-benefício
                </span>
              )}
              <h3 className="text-2xl" style={{ ...SERIFA, color: COR.titulo }}>{p.nome}</h3>
              <div className="mt-4 flex items-end gap-1">
                <span className="text-4xl font-bold" style={{ color: COR.texto }}>{p.precoTexto}</span>
                <span className="pb-1" style={{ color: COR.suave }}>{p.periodo}</span>
              </div>
              <div className="mt-2 h-6 text-sm" style={{ color: COR.coral }}>
                {p.porMes ? `${p.porMes} · ${p.economia}` : ""}
              </div>
              <ul className="mt-5 flex-1 space-y-2 text-sm" style={{ color: COR.suave }}>
                <li className="flex gap-2"><Check className="h-4 w-4 shrink-0" style={{ color: COR.coral }} />Todas as funções do app</li>
                <li className="flex gap-2"><Check className="h-4 w-4 shrink-0" style={{ color: COR.coral }} />{p.descricao}</li>
                <li className="flex gap-2"><Check className="h-4 w-4 shrink-0" style={{ color: COR.coral }} />Cancele quando quiser</li>
              </ul>
              <button
                onClick={() => assinar(p.id)}
                disabled={carregando !== null}
                className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-md px-5 py-3 font-semibold transition-transform hover:-translate-y-0.5 disabled:opacity-60"
                style={{ background: p.destaque ? COR.coral : "transparent", color: p.destaque ? COR.fundo : COR.titulo, border: `1px solid ${COR.coral}` }}
                data-testid={`assinar-${p.id}`}
              >
                {carregando === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {carregando === p.id ? "Abrindo pagamento..." : `Assinar ${p.nome}`}
              </button>
            </div>
          ))}
        </div>
        <div className="mx-auto mt-8 grid max-w-4xl gap-4 text-sm sm:grid-cols-3" style={{ color: COR.suave }}>
          <div className="flex items-start gap-2"><Lock className="h-5 w-5 shrink-0" style={{ color: COR.coral }} />Pagamento seguro pelo Stripe. Os dados do cartão não passam pelo app.</div>
          <div className="flex items-start gap-2"><Zap className="h-5 w-5 shrink-0" style={{ color: COR.coral }} />Acesso liberado logo depois da confirmação do pagamento.</div>
          <div className="flex items-start gap-2"><CreditCard className="h-5 w-5 shrink-0" style={{ color: COR.coral }} />Cartão de crédito com renovação automática. Sem multa para cancelar.</div>
        </div>
      </Secao>

      {/* Beneficios */}
      <Secao alt>
        <div className="text-center">
          <Titulo>Benefícios Que Você Não Encontra em Outro Lugar</Titulo>
        </div>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { icon: Smartphone, t: "Interface intuitiva", d: "Fácil de usar mesmo para quem não tem familiaridade com tecnologia. Em minutos você domina o sistema." },
            { icon: TrendingUp, t: "Lucro, não só faturamento", d: "Não erre mais na precificação. Garanta LUCRO, e não apenas faturamento." },
            { icon: RefreshCw, t: "Atualizações constantes", d: "O sistema está sempre evoluindo, e as melhorias entram na sua assinatura sem custo a mais." },
            { icon: CalendarX, t: "Sem fidelidade", d: "Cancele quando quiser pela área Minha conta, sem multa e sem burocracia." },
          ].map((b) => (
            <div key={b.t} className="rounded-xl border p-6" style={{ borderColor: COR.borda, background: COR.fundo }}>
              <b.icon className="h-7 w-7" style={{ color: COR.coral }} />
              <h3 className="mt-4 text-xl" style={{ ...SERIFA, color: COR.titulo }}>{b.t}</h3>
              <p className="mt-2 leading-relaxed" style={{ color: COR.suave }}>{b.d}</p>
            </div>
          ))}
        </div>
      </Secao>

      {/* Duvidas */}
      <Secao id="duvidas">
        <div className="text-center">
          <Titulo>Dúvidas Frequentes</Titulo>
        </div>
        <Accordion type="single" collapsible className="mx-auto mt-8 max-w-3xl">
          {PERGUNTAS.map((q, i) => (
            <AccordionItem key={q.p} value={`q${i}`} style={{ borderColor: COR.borda }}>
              <AccordionTrigger className="text-left text-lg hover:no-underline" style={{ color: COR.titulo }}>{q.p}</AccordionTrigger>
              <AccordionContent className="text-base leading-relaxed" style={{ color: COR.suave }}>{q.r}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Secao>

      {/* Chamada final */}
      <Secao alt>
        <div className="text-center">
          <Titulo>Transforme Sua Clínica Agora Mesmo!</Titulo>
          <p className="mx-auto mt-4 max-w-2xl text-lg" style={{ color: COR.suave }}>
            Não perca mais tempo nem dinheiro com uma gestão desorganizada. Comece hoje a cobrar o preço certo por cada
            procedimento.
          </p>
          <div className="mt-8"><BotaoPlanos>Escolher meu plano</BotaoPlanos></div>
        </div>
      </Secao>

      {/* Rodape */}
      <footer className="border-t px-4 py-10 text-sm" style={{ borderColor: COR.borda, background: COR.fundo, color: COR.suave }}>
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 md:flex-row">
          <div className="text-center md:text-left">
            <div style={{ ...SERIFA, color: COR.titulo }}>Aesthetica Pricing Pro</div>
            {empresa?.nome && <div>{empresa.nome}{empresa.documento ? ` · ${empresa.documento}` : ""}</div>}
            {(empresa?.suporte_email || empresa?.email_contato) && (
              <div className="text-xs">
                Dúvidas? <a className="underline" href={`mailto:${empresa.suporte_email || empresa.email_contato}`}>{empresa.suporte_email || empresa.email_contato}</a>*
              </div>
            )}
          </div>
          <nav className="flex flex-wrap justify-center gap-5">
            <Link to="/termos" className="hover:underline">Termos de Uso</Link>
            <Link to="/privacidade" className="hover:underline">Política de Privacidade</Link>
            <Link to="/login" className="hover:underline">Entrar</Link>
          </nav>
        </div>
        {(empresa?.suporte_email || empresa?.email_contato) && (
          <p className="mx-auto mt-6 max-w-6xl text-center text-[11px] opacity-80 md:text-left">
            *Respondemos por e-mail em dias úteis. Em períodos de maior procura, a resposta pode levar um pouco mais.
          </p>
        )}
      </footer>

      {carregando && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="flex items-center gap-3 rounded-lg px-6 py-4" style={{ background: COR.cartao }}>
            <Loader2 className="h-5 w-5 animate-spin" style={{ color: COR.coral }} /> Abrindo o pagamento seguro...
          </div>
        </div>
      )}
    </div>
  );
}
